import { Link } from 'react-router-dom';
import { useState } from 'react';

const ALL_POSTS = [
  {
    category: 'AI & Travel',
    title: 'How AI is Transforming the Travel Agency Industry in 2025',
    excerpt: 'Agencies that adopted AI early are seeing 40% more bookings. Here is what is changing and how to stay ahead of the curve.',
    date: 'March 18, 2025',
    readTime: '5 min read',
    slug: 'ai-transforming-travel-agency-industry-2025',
    image: 'https://public.readdy.ai/ai/img_res/3d98d7c4baab35f2ee3e35611c6feca7.jpg',
    featured: true,
  },
  {
    category: 'WhatsApp',
    title: 'Automating WhatsApp Bookings: A Step-by-Step Guide for Travel Agents',
    excerpt: 'WhatsApp is where your customers already are. Learn how to automate replies, confirmations, and follow-ups without losing the personal touch.',
    date: 'March 10, 2025',
    readTime: '7 min read',
    slug: 'automating-whatsapp-bookings-travel-agents',
    image: 'https://readdy.ai/api/search-image?query=clean%20minimalist%20workspace%20showing%20mobile%20phone%20with%20messaging%20app%20open%20professional%20desk%20setting%20muted%20neutral%20tones%20modern%20B2B%20environment&width=800&height=480&seq=blogidx2&orientation=landscape',
    featured: false,
  },
  {
    category: 'Conversion',
    title: 'Increasing Booking Conversions with AI-Powered Follow-Up Sequences',
    excerpt: 'Most bookings are lost due to slow follow-up. Discover how automated sequences keep leads warm and guide them to confirmation.',
    date: 'February 28, 2025',
    readTime: '6 min read',
    slug: 'increasing-booking-conversions-ai-follow-up',
    image: 'https://readdy.ai/api/search-image?query=professional%20business%20analytics%20dashboard%20screen%20showing%20growth%20charts%20clean%20modern%20office%20environment%20minimalist%20design%20neutral%20background%20B2B%20SaaS&width=800&height=480&seq=blogidx3&orientation=landscape',
    featured: false,
  },
];

const CATEGORIES = ['All', 'AI & Travel', 'WhatsApp', 'Conversion'];

const SITE_URL = import.meta.env.VITE_SITE_URL || 'https://trewaz.com';

const blogSchema = {
  '@context': 'https://schema.org',
  '@type': 'Blog',
  name: 'Trewaz Blog — Insights & Automation Strategies for Travel Agencies',
  description: 'Practical guides, tips, and insights for travel agents on AI booking automation, WhatsApp marketing, CRM systems, and increasing booking conversions.',
  url: `${SITE_URL}/blog`,
  publisher: {
    '@type': 'Organization',
    name: 'Trewaz',
    url: SITE_URL,
  },
  blogPost: [
    {
      '@type': 'BlogPosting',
      headline: 'How AI is Transforming the Travel Agency Industry in 2025',
      url: `${SITE_URL}/blog/ai-transforming-travel-agency-industry-2025`,
      datePublished: '2025-03-18',
      dateModified: '2025-03-18',
      author: { '@type': 'Organization', name: 'Trewaz Editorial Team' },
      description: 'Discover how AI is changing the way travel agencies work in 2025. Learn how AI booking automation, smart CRM, and instant WhatsApp replies are helping agencies close 40% more bookings.',
      image: 'https://readdy.ai/api/search-image?query=modern%20minimalist%20office%20workspace%20with%20laptop%20showing%20data%20analytics%20dashboard%20professional%20B2B%20technology%20setting%20clean%20white%20interior%20sophisticated&width=800&height=480&seq=blogidx1&orientation=landscape',
    },
    {
      '@type': 'BlogPosting',
      headline: 'Automating WhatsApp Bookings: A Step-by-Step Guide for Travel Agents',
      url: `${SITE_URL}/blog/automating-whatsapp-bookings-travel-agents`,
      datePublished: '2025-03-10',
      dateModified: '2025-03-10',
      author: { '@type': 'Organization', name: 'Trewaz Editorial Team' },
      description: 'Learn how to automate WhatsApp replies, booking confirmations, and follow-ups for your travel agency. A practical step-by-step guide for travel agents.',
      image: 'https://readdy.ai/api/search-image?query=clean%20minimalist%20workspace%20showing%20mobile%20phone%20with%20messaging%20app%20open%20professional%20desk%20setting%20muted%20neutral%20tones%20modern%20B2B%20environment&width=800&height=480&seq=blogidx2&orientation=landscape',
    },
    {
      '@type': 'BlogPosting',
      headline: 'Increasing Booking Conversions with AI-Powered Follow-Up Sequences',
      url: `${SITE_URL}/blog/increasing-booking-conversions-ai-follow-up`,
      datePublished: '2025-02-28',
      dateModified: '2025-02-28',
      author: { '@type': 'Organization', name: 'Trewaz Editorial Team' },
      description: 'Most travel agencies lose bookings due to slow or inconsistent follow-up. Discover how AI-powered follow-up sequences help travel agents convert more leads into confirmed bookings.',
      image: 'https://readdy.ai/api/search-image?query=professional%20business%20analytics%20dashboard%20screen%20showing%20growth%20charts%20clean%20modern%20office%20environment%20minimalist%20design%20neutral%20background%20B2B%20SaaS&width=800&height=480&seq=blogidx3&orientation=landscape',
    },
  ],
};

export default function BlogIndexPage() {
  const [activeCategory, setActiveCategory] = useState('All');

  const filtered = activeCategory === 'All'
    ? ALL_POSTS
    : ALL_POSTS.filter((p) => p.category === activeCategory);

  const featured = ALL_POSTS.find((p) => p.featured);
  const rest = filtered.filter((p) => !p.featured || activeCategory !== 'All');

  return (
    <>
      <title>Blog — AI Automation Insights for Travel Agencies | Trewaz</title>
      <meta name="description" content="Practical guides, tips, and insights for travel agents on AI booking automation, WhatsApp marketing, CRM systems, and increasing booking conversions. Learn from the Trewaz blog." />
      <meta name="keywords" content="travel agency automation blog, AI travel agent tips, WhatsApp booking automation, travel CRM guide, travel agency software insights, increase travel bookings" />
      <link rel="canonical" href={`${SITE_URL}/blog`} />
      <meta name="robots" content="index, follow" />
      <meta property="og:type" content="website" />
      <meta property="og:title" content="Blog — AI Automation Insights for Travel Agencies | Trewaz" />
      <meta property="og:description" content="Practical guides for travel agents on AI booking automation, WhatsApp marketing, CRM systems, and increasing conversions." />
      <meta property="og:url" content={`${SITE_URL}/blog`} />
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:title" content="Blog — AI Automation Insights for Travel Agencies | Trewaz" />
      <meta name="twitter:description" content="Practical guides for travel agents on AI automation, WhatsApp bookings, CRM, and conversions." />
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(blogSchema) }}
      />

      <div className="min-h-screen bg-white font-sans">
        {/* Navbar */}
        <header className="sticky top-0 z-50 bg-white/95 backdrop-blur-sm border-b border-gray-100">
          <div className="max-w-7xl mx-auto px-6 h-16 flex items-center justify-between">
            <Link to="/" className="text-xl font-bold tracking-tight text-gray-900 cursor-pointer">Trewaz</Link>
            <nav className="hidden md:flex items-center gap-7">
              <Link to="/#features" className="text-sm text-gray-600 hover:text-gray-900 transition-colors cursor-pointer">Features</Link>
              <Link to="/#how-it-works" className="text-sm text-gray-600 hover:text-gray-900 transition-colors cursor-pointer">How It Works</Link>
              <Link to="/#pricing" className="text-sm text-gray-600 hover:text-gray-900 transition-colors cursor-pointer">Pricing</Link>
              <Link to="/blog" className="text-sm font-medium text-gray-900 cursor-pointer">Blog</Link>
            </nav>
            <div className="flex items-center gap-4">
              <Link to="/login" className="text-sm text-gray-600 hover:text-gray-900 transition-colors cursor-pointer whitespace-nowrap">Login</Link>
              <Link to="/register" className="text-sm font-medium bg-gray-900 text-white px-4 py-2 rounded-md hover:bg-gray-700 transition-colors cursor-pointer whitespace-nowrap">Start Free Trial</Link>
            </div>
          </div>
        </header>

        {/* Page Header */}
        <section className="pt-20 pb-12 px-6">
          <div className="max-w-7xl mx-auto">
            <p className="text-sm font-medium uppercase tracking-wider mb-3 bg-gradient-to-r from-teal-500 to-indigo-500 bg-clip-text text-transparent">Blog</p>
            <h1 className="text-4xl md:text-5xl font-bold text-gray-900 leading-tight tracking-tight mb-4">
              Insights & Automation<br className="hidden md:block" /> Strategies
            </h1>
            <p className="text-lg text-gray-500 max-w-xl">
              Practical guides for travel agents on AI automation, WhatsApp bookings, CRM, and growing your agency.
            </p>
          </div>
        </section>

        {/* Featured Article */}
        {featured && activeCategory === 'All' && (
          <section className="px-6 mb-16">
            <div className="max-w-7xl mx-auto">
              <Link
                to={`/blog/${featured.slug}`}
                className="group grid grid-cols-1 lg:grid-cols-2 gap-0 border border-gray-100 rounded-2xl overflow-hidden hover:border-gray-200 transition-all cursor-pointer block"
              >
                <div className="w-full h-64 lg:h-auto bg-gray-100 overflow-hidden">
                  <img src={featured.image} alt={featured.title} className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-700" />
                </div>
                <div className="p-8 lg:p-12 flex flex-col justify-center">
                  <div className="flex items-center gap-2 mb-4">
                    <span className="text-xs font-medium bg-gradient-to-r from-teal-500 to-indigo-500 bg-clip-text text-transparent border border-teal-200 rounded-full px-3 py-0.5">{featured.category}</span>
                    <span className="text-xs text-gray-400">{featured.readTime}</span>
                    <span className="text-xs bg-gray-100 text-gray-500 rounded-full px-2.5 py-0.5">Featured</span>
                  </div>
                  <h2 className="text-2xl md:text-3xl font-bold text-gray-900 leading-tight mb-4 group-hover:text-teal-600 transition-colors">{featured.title}</h2>
                  <p className="text-gray-500 text-sm leading-relaxed mb-6">{featured.excerpt}</p>
                  <div className="flex items-center justify-between">
                    <span className="text-xs text-gray-400">{featured.date}</span>
                    <span className="text-sm font-medium text-gray-900 flex items-center gap-1.5 group-hover:gap-2.5 transition-all">
                      Read article <i className="ri-arrow-right-line" />
                    </span>
                  </div>
                </div>
              </Link>
            </div>
          </section>
        )}

        {/* Filter Categories */}
        <section className="px-6 mb-10">
          <div className="max-w-7xl mx-auto">
            <div className="flex items-center gap-2 flex-wrap">
              {CATEGORIES.map((cat) => (
                <button
                  key={cat}
                  onClick={() => setActiveCategory(cat)}
                  className={`text-sm px-4 py-1.5 rounded-full border transition-all cursor-pointer whitespace-nowrap ${
                    activeCategory === cat
                      ? 'bg-gray-900 text-white border-gray-900'
                      : 'text-gray-600 border-gray-200 hover:border-gray-400'
                  }`}
                >
                  {cat}
                </button>
              ))}
            </div>
          </div>
        </section>

        {/* Articles Grid */}
        <section className="px-6 pb-24">
          <div className="max-w-7xl mx-auto">
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
              {rest.map((post) => (
                <Link
                  key={post.slug}
                  to={`/blog/${post.slug}`}
                  className="group bg-white border border-gray-100 rounded-xl overflow-hidden hover:border-gray-200 transition-all cursor-pointer block"
                >
                  <div className="w-full h-48 overflow-hidden bg-gray-100">
                    <img src={post.image} alt={post.title} className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-500" />
                  </div>
                  <div className="p-5 flex flex-col gap-3">
                    <div className="flex items-center gap-2">
                      <span className="text-xs font-medium text-gray-500 bg-gray-100 rounded-full px-2.5 py-0.5">{post.category}</span>
                      <span className="text-xs text-gray-400">{post.readTime}</span>
                    </div>
                    <h3 className="text-sm font-semibold text-gray-900 leading-snug group-hover:text-teal-600 transition-colors">{post.title}</h3>
                    <p className="text-xs text-gray-500 leading-relaxed">{post.excerpt}</p>
                    <div className="flex items-center justify-between pt-1 border-t border-gray-50 mt-auto">
                      <span className="text-xs text-gray-400">{post.date}</span>
                      <span className="text-xs font-medium text-gray-800 flex items-center gap-1 group-hover:gap-2 transition-all">
                        Read more <i className="ri-arrow-right-line" />
                      </span>
                    </div>
                  </div>
                </Link>
              ))}
            </div>

            {filtered.length === 0 && (
              <div className="text-center py-24">
                <p className="text-gray-400 text-sm">No articles in this category yet. Check back soon.</p>
              </div>
            )}
          </div>
        </section>

        {/* Footer */}
        <footer className="bg-gray-50 border-t border-gray-100 py-10">
          <div className="max-w-7xl mx-auto px-6 flex flex-col sm:flex-row items-center justify-between gap-4">
            <span className="text-sm font-bold text-gray-900">Trewaz</span>
            <p className="text-xs text-gray-400">© {new Date().getFullYear()} Trewaz. All rights reserved.</p>
            <div className="flex items-center gap-5">
              <Link to="/blog" className="text-xs text-gray-500 hover:text-gray-900 transition-colors cursor-pointer">Blog</Link>
              <a href="#" className="text-xs text-gray-500 hover:text-gray-900 transition-colors cursor-pointer">Privacy Policy</a>
              <a href="#" className="text-xs text-gray-500 hover:text-gray-900 transition-colors cursor-pointer">Terms</a>
            </div>
          </div>
        </footer>
      </div>
    </>
  );
}
